<template>
    <div class="widget-wrap">
        <div :class="['widget', widgetClass, isExpanded ? 'expanded' : 'normal']">
            <header @dblclick.prevent="toggleExpandCollapseWidget()">
                <div class="widget-header-left-part">
                    <h4 :class="{ warning: needsAttention }">
                        <i v-if="needsAttention" class="icon-allert"> </i>
                        <div
                            v-if="needsAttention"
                            ref="tooltiptext"
                            :class="[
                                'tooltiptext',
                                { 'for-not-expanded': !isExpanded && settingNames.length > 0 },
                            ]"
                            @mouseenter.stop.prevent
                            @mousedown.stop.prevent
                        >
                            {{ $t('dashboard.widgetHasrenamedOrRemovedLocations') }}
                        </div>
                        {{ widgetName }}
                    </h4>
                    <div class="widget-setting-list print-only">
                        <div
                            v-for="(item, index) in configurationData"
                            :key="index"
                            class="widget-tooltiptext-item"
                        >
                            <div class="widget-tooltiptext-label">{{ $t(item.label) }}:</div>
                            <div class="widget-tooltiptext-value">{{ item.value }}</div>
                        </div>
                    </div>
                    <div
                        v-if="!isExpanded && settingNames.length > 0"
                        class="widget-setting-list no-print"
                        @mouseenter.stop.prevent="settingNamesHover"
                        @mousedown.stop.prevent
                    >
                        <i class="icon-info"></i>
                        <span class="widget-setting-names">
                            {{ settingNames.join(', ') }}
                        </span>
                        <div
                            ref="tooltiptext"
                            class="tooltiptext"
                            @mouseenter.stop.prevent
                            @mousedown.stop.prevent
                        >
                            <Simplebar class="with-border-track">
                                <div
                                    v-for="(item, index) in configurationData"
                                    :key="index"
                                    class="widget-tooltiptext-item"
                                >
                                    <p class="widget-tooltiptext-label">{{ $t(item.label) }}:</p>
                                    <p class="widget-tooltiptext-value">{{ item.value }}</p>
                                </div>
                            </Simplebar>
                        </div>
                    </div>
                </div>
                <div class="buttons" @mousedown.stop.prevent>
                    <button
                        v-if="!isExpanded"
                        :data-tooltip="$t('common.expand')"
                        class="with-tooltip expansion-button"
                        @click="$emit('expand', id, true)"
                    >
                        <i class="icon-expand"></i>
                    </button>
                    <button
                        v-else
                        :data-tooltip="$t('common.collapse')"
                        class="with-tooltip expansion-button"
                        @click="$emit('collapse')"
                    >
                        <i class="icon-collapse"></i>
                    </button>
                    <button :data-tooltip="$t('common.edit')" class="with-tooltip" @click="$emit('edit', id)">
                        <i class="icon-edit"></i>
                    </button>
                    <button
                        :data-tooltip="$t('common.delete')"
                        class="with-tooltip"
                        @click="$emit('delete', id)"
                    >
                        <i class="icon-delete"></i>
                    </button>
                </div>
            </header>
            <div class="widget-body">
                <WidgetConfigurationTable :data="configurationData" />
                <WidgetLastUpdated :last-updated="widgetLastUpdated" />
                <div v-show="isLoading" class="widget-loader-wrapper">
                    <Loader :visible="isLoading" css-class="widget-loader" />
                </div>
                <slot v-if="hasContent && !needsAttention"></slot>
                <div v-if="!isLoading && !hasContent && !needsAttention" class="no-widget">
                    <div class="no-widget-data">
                        <slot name="empty">
                            <h2>{{ $t('common.noData') }}</h2>
                            <p>{{ $t('dashboard.otherConfiguration') }}</p>
                        </slot>
                    </div>
                </div>
                <div v-if="!isLoading && needsAttention" class="no-widget">
                    <div class="no-widget-data">
                        <slot name="empty">
                            <h2>{{ $t('common.noData') }}</h2>
                            <p>{{ $t('dashboard.wrongConfiguration') }}</p>
                            <p>{{ $t('dashboard.widgetNeedsEditing') }}</p>
                        </slot>
                    </div>
                </div>
                <div v-if="isLoading && !hasContent" class="print-only">
                    <div class="no-widget">
                        <h2>{{ $t('common.dataIsLoading') }}</h2>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Simplebar from 'simplebar-vue';
import WidgetLastUpdated from './widgets/common/WidgetLastUpdated';
import WidgetConfigurationTable from './widgets/common/WidgetConfigurationTable.vue';
import Loader from '../common/Loader.vue';
import { WIDGET_TYPES } from '../../constants';

export default {
    components: {
        Loader,
        Simplebar,
        WidgetConfigurationTable,
        WidgetLastUpdated,
    },

    props: {
        id: {
            type: Number,
            required: true,
        },

        widgetName: {
            type: String,
            default: '',
        },

        widgetType: {
            type: Number,
            required: true,
        },

        isLoading: {
            type: Boolean,
            required: true,
        },

        isExpanded: {
            type: Boolean,
            required: true,
        },

        hasContent: {
            type: Boolean,
            required: true,
        },

        configurationData: {
            type: Array,
            default: () => [],
        },

        widgetLastUpdated: {
            validator: prop => prop instanceof Date || prop === null,
            default: null,
            required: true,
        },

        needsAttention: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        settingNames() {
            return this.configurationData.map(item => this.$t(item.label));
        },

        tooltipText() {
            return this.settingNames.map(name => name).join('<br>');
        },

        widgetClass() {
            let type = this.widgetType;

            let widgetNames = Object.keys(WIDGET_TYPES);
            let widgetKeys = Object.values(WIDGET_TYPES);

            if (widgetKeys.includes(type)) {
                let widgetTypeName = widgetNames[widgetKeys.indexOf(type)];

                // SystemActivity -> system-activity
                // NursePresence -> nurse-presence
                // Assignments -> assignments
                return widgetTypeName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
            }

            return '';
        },
    },

    methods: {
        toggleExpandCollapseWidget() {
            return this.isExpanded ? this.$emit('collapse') : this.$emit('expand', this.id, true);
        },
        settingNamesHover() {
            if (this.$refs.hasOwnProperty('tooltiptext')) {
                let tooltiptext = this.$refs.tooltiptext;
                tooltiptext.style.height = 'auto';

                let height = tooltiptext.clientHeight + 1;
                tooltiptext.style.height = `${height}px`;
            }
        },
    },
};
</script>
